import { FindestButton } from "Components";
import { FindestTextBox } from "Components/Shared/Inputs";
import { CaseControllerSingleton, MediaControllerSingleton } from "Controllers";
import { CaseActionTypeEnum, ToastTypeEnum } from "Enums";
import { ToastHelperSingleton } from "Helpers";
import { ICaseSelectedAction } from "Interfaces";
import React, { FC, useContext, useRef, useState } from "react";
import { CaseContext } from "Stores";

// styles
import buttonStyles from "styles/buttons.module.scss";
import modalStyles from "styles/modal.module.scss";
import styles from "./caseEditHeaderModal.module.scss";

type TCaseEditHeaderModalProps = {
    caseId: number,
    caseTitle: string,
    caseImageUrl?: string | null,
    caseColorString?: string | null,
    onCloseModal: () => void
}

export const CaseEditHeaderModal: FC<TCaseEditHeaderModalProps> = ({ caseId, caseTitle, caseImageUrl, caseColorString, onCloseModal }: TCaseEditHeaderModalProps) => {

    // Constants
    const caseImageRadioName = "CaseImageType";
    const caseImageTypeUrl = "CaseImageTypeUrl";
    const caseImageTypeLocal = "CaseImageTypeLocal";
    const caseImageTypeNone = "CaseImageTypeNone";

    // Contexts
    const { caseState, caseDispatch } = useContext(CaseContext);

    // Refs
    const fileInputRef = useRef<HTMLInputElement>(null);

    // State
    const [selectedCaseImageType, setSelectedCaseImageType] = useState(caseImageUrl ? caseImageTypeUrl : caseImageTypeNone);
    const [previewImageSource, setPreviewImageSource] = useState(caseImageUrl ? `/assets/images/cases/${caseImageUrl}` : undefined);
    const [externalImageUrl, setExternalImageUrl] = useState("");
    const [newCaseTitle, setNewCaseTitle] = useState(caseTitle);
    const [isImageLoaded, setIsImageLoaded] = useState<boolean>(false);

    const openFileSelector = () => {
        if (!fileInputRef.current) return;

        fileInputRef.current.click();
    };

    const onFileSelected = () => {
        if (!fileInputRef.current || !fileInputRef.current.files) return;

        setPreviewImageSource(URL.createObjectURL(fileInputRef.current.files[0]));
    };

    const onImageUrlChanged = (text: string) => {
        setPreviewImageSource(text);
        setExternalImageUrl(text);
        if (text === "") {
            setIsImageLoaded(false);
        }
    };

    const onCaseTitleChanged = (text: string) => {
        setNewCaseTitle(text);
    };

    const imageTypeChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setExternalImageUrl("");
        setPreviewImageSource(undefined);
        setIsImageLoaded(false);

        setSelectedCaseImageType(event.target.value);
    };

    const saveCaseChanges = async () => {

        if (newCaseTitle.length === 0) {
            ToastHelperSingleton.showToast(ToastTypeEnum.Error, "A case title is required.");
            return;
        }

        const title = newCaseTitle.trim() === caseTitle.trim() ? undefined : newCaseTitle.trim();
        let imageFile = null;

        if (selectedCaseImageType === caseImageTypeUrl) {
            const imageUrl = externalImageUrl.trim().length === 0 ? undefined : externalImageUrl.trim();
            if (imageUrl) {
                imageFile = await MediaControllerSingleton.DownloadImageUrl(imageUrl);
            }
        } else if (selectedCaseImageType === caseImageTypeLocal) {
            if (fileInputRef.current && fileInputRef.current.files && fileInputRef.current.files.length > 0) {
                imageFile = fileInputRef.current.files[0];
            }
        }

        const updateHeaderInformation = await CaseControllerSingleton.updateHeaderInfo(caseId, title, imageFile, 
            selectedCaseImageType === caseImageTypeNone);
        
        onCloseModal();

        const currentCase = caseState.selectedCase;
        if(currentCase) {
            currentCase.title = updateHeaderInformation.title;
            currentCase.caseImage = updateHeaderInformation.imagePath;
            caseDispatch({ type: CaseActionTypeEnum.SetSelectedCase, newSelectedCase: currentCase } as ICaseSelectedAction);
        }
    };

    const onImageError = () => {
        if (isImageLoaded) {
            setIsImageLoaded(false);
        }
    };

    const onImageLoad = () => {
        if (!isImageLoaded) {
            setIsImageLoaded(true);
        }
    };

    return (
        <div className={styles.caseEditHeaderModal}>
            <div>
                <div className={styles.section}>
                    <div className={modalStyles.title}>Case title:</div>
                    <FindestTextBox value={newCaseTitle} onChange={onCaseTitleChanged} />
                </div>
                <div className={styles.section}>
                    <div className={modalStyles.title}>Case image preview:</div>
                    {previewImageSource ?
                        <span>
                            <img
                                src={previewImageSource}
                                alt="Case preview"
                                className={`${styles.previewImage} ${isImageLoaded ? styles.show : styles.hide }`}
                                onError={onImageError}
                                onLoad={onImageLoad}
                            />
                            <div className={`${styles.noImagePreview} ${isImageLoaded ? styles.hide : styles.show }`} />
                        </span>
                        : caseColorString ?
                            <div className={styles.imageLessColorBackground} style={{ backgroundColor: caseColorString }} />
                            : <div className={styles.noImagePreview} />}

                </div>
                <div className={styles.section}>
                    <div className={modalStyles.title}>Change case image:</div>
                    <div>
                        <div className={styles.caseImageOption}>
                            <label className={styles.radioButton}>
                                <input onChange={imageTypeChanged} type="radio" name={caseImageRadioName} value={caseImageTypeUrl} checked={selectedCaseImageType === caseImageTypeUrl} /> <div className={styles.radioButton}>Image URL</div>
                            </label>
                            {selectedCaseImageType === caseImageTypeUrl ? <FindestTextBox placeholder="URL" onChange={onImageUrlChanged} value={externalImageUrl} extraClassName={styles.caseImageURLTextBox} /> : null}
                        </div>
                        <div className={`${styles.caseImageOption} ${styles.caseImageLocalOption}`}>
                            <label className={styles.radioButton}>
                                <input onChange={imageTypeChanged} type="radio" name={caseImageRadioName} value={caseImageTypeLocal} checked={selectedCaseImageType === caseImageTypeLocal} /> <div className={styles.radioButton}>Local image</div>
                            </label>
                            {selectedCaseImageType === caseImageTypeLocal ? <FindestButton title="CHOOSE FILE" onClick={openFileSelector} extraClassName={styles.fileUploadButton} /> : null}
                            <input type="file" style={{ display: "none" }} ref={fileInputRef} onChange={onFileSelected} />
                        </div>
                        <div className={styles.caseImageOption}>
                            <label className={styles.radioButton}>
                                <input onChange={imageTypeChanged} type="radio" name={caseImageRadioName} value={caseImageTypeNone} checked={selectedCaseImageType === caseImageTypeNone} /><div className={styles.radioButton}>No image</div>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
            <div className={modalStyles.marginTop}>
                <FindestButton extraClassName={`${buttonStyles.coloredButton} ${buttonStyles.purple} ${styles.saveButton}`} title="Save Changes" onClick={saveCaseChanges} />
                <FindestButton extraClassName={buttonStyles.cancelButton} title="Cancel" onClick={onCloseModal} />
            </div>
        </div>
    );
};